// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtThread.tsx
//
// Consolidated threaded-comments renderer. One pair of mutually-recursive components owns the entire
// reply rail so the connector geometry is defined in a single place and is identical in every state:
//   - ThreadChildrenBlock draws the per-child connector cell (incoming line, elbow, continuing line)
//     for a list of sibling replies and recurses into each child.
//   - ThoughtThreadNode renders one reply: its avatar-down line + clickable collapse rail, the
//     argument body (full) OR a header-only collapsed row, then its own children block, and a
//     "Continue this thread" link when the node has more replies than were fetched (the visual cap).
//
// Data comes from a single subtree fetch (see ThoughtReplyTree), so the whole visible tree renders
// at once — no per-level fetch, no staggered reveal — and collapse/expand is pure client state.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Minus } from "lucide-react";
import ThoughtDisplayerInDiscussion from "@/main/react/thoughts/ThoughtDisplayerInDiscussion";
import ThoughtTextEditor from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import type { EditorHandle as ThoughtTextEditorHandle } from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { applyQuoteWhenReady, showThoughtTextEditorDialog } from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type { DialogController } from "@/main/react/Dialog/Dialog";
import { threadConnectorElbowClass, threadConnectorLineClass } from "@/main/react/thoughts/replyThreadConnectorStyles";
import { ThoughtInDiscussionDTO } from "@/generated-client";
import { MetaHandler } from "@/main/common/MetaHandler";
import { useIsMobile } from "@/hooks/use-mobile";
import { Localizer } from "@/main/react/Types";
import { thoughtPermalinkHref } from "@/main/react/thoughts/thoughtChainRoute";

export type ReplyTreeCommonProps = {
    discussionPublicId: string;
    discussionSlug: string;
    // Title of the host post, for the share payload only (BL-083). It rides in the COMMON bag rather
    // than as a per-node prop for the same reason `discussionSlug` does: the reply rail is recursive,
    // so one field here covers every depth instead of one prop per level.
    discussionTitle?: string;
    // The post author's username (round 12 `2d`), for the "Author" pill on reply bylines. In the common
    // bag for the same reason as `discussionTitle`: one field covers every depth of the recursive rail.
    discussionAuthorUsername?: string | null;
    localizer: Localizer;
    loggedInUsername?: string;
    agreementAgreementTitle: string;
    disagreementAgreementTitle: string;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
};

export type ThreadNode = ThoughtInDiscussionDTO & {
    replyCount?: number;
    descendantCount?: number;
    depth?: number;
    parentArgumentPublicId?: string | null;
};

export type ChildrenByParent = Map<string, ThreadNode[]>;

// Hard ceiling on how deep the reply rail will nest before it stops recursing. The server subtree
// fetch is already bounded (maxDepth, default 3; deeper threads show "Continue this thread"), so a
// real thread never approaches this. It exists only as belt-and-suspenders against a malformed /
// pathologically deep payload so the render can never overflow the JS call stack. Paired with the
// ancestor-cycle guard below (which catches the far more likely case: a reply that loops back onto
// an ancestor). Prod RangeError, 2026-07-21 — see designs/08-audits-and-process/2026-07-21-*.
const MAX_RENDER_DEPTH = 40;

// How far one nesting level pushes its children to the right. These MIRROR the Tailwind utilities on
// ThreadChildrenBlock's wrapper and row below — `ml-4 sm:ml-5` + the `w-6` connector column + `gap-2` —
// and exist so the reply composer can cancel the accumulated indent and use the full card width.
//
// They are published as a CSS custom property by the SAME component that applies those classes (see
// THREAD_INDENT_CSS_VAR below), so the value can never drift from the geometry it describes: change one
// and you are standing in the code that must change the other. Anything that needs the running total
// reads the variable; nothing else may re-derive it.
export const LEVEL_INDENT_PX = { mobile: 16 + 24 + 8, desktop: 20 + 24 + 8 } as const;

// An indent has TWO sides, and until 2026-08-07 only the left one was accounted for. The content cell
// below carries `mr-2`, so every nesting level also pulls the usable RIGHT edge inward by 8px. The
// composer cancels the left indent to reach the card's left edge; without cancelling this it stops 8px
// per level short of the card's right edge, and "full card width" holds on one side only.
//
// This is the whole reason the width half of `discussion-reply-composer-disclosure-and-width.spec.mjs`
// failed on its first devo run (see the design's §11.8) — mirrors `mr-2` on the content cell below, and
// is published by the same component for the same reason `LEVEL_INDENT_PX` is.
export const LEVEL_RIGHT_INSET_PX = 8;

/** Running left-indent of the current nesting level, in px. Inherited, so descendants see their own. */
export const THREAD_INDENT_CSS_VAR = "--thread-indent";

/** Running right-inset of the current nesting level, in px. The mirror of THREAD_INDENT_CSS_VAR. */
export const THREAD_RIGHT_INSET_CSS_VAR = "--thread-right-inset";

type ThreadContext = {
    common: ReplyTreeCommonProps;
    childrenByParent: ChildrenByParent;
    collapsedIds: Set<string>;
    onToggleCollapsed: (argumentPublicId: string, collapsed: boolean) => void;
    onReplyPublished: () => void;
    // The ONE node in this tree whose inline composer is open, or null. Held by the tree owner
    // (ThoughtReplyTree) rather than as per-node local state so a second Reply click closes the first
    // composer instead of stacking another one: a member could otherwise leave five composers open down
    // one thread, type into one, scroll, and lose track of which. Desktop only — mobile opens a dialog
    // per node and is unaffected. The in-progress text is not lost when a composer closes;
    // thoughtDraftStore persists it keyed by (discussion, parent, account) and restores on reopen.
    activeReplyPublicId: string | null;
    // Toggle intent for a node's composer. The owner decides what "open B while A is open" means, so
    // the rule lives in one place instead of being restated per node.
    onReplyToggled: (argumentPublicId: string) => void;
};

// Renders the connector cells for a list of sibling replies. The avatar-down line that feeds the
// first cell is drawn by the caller (ThoughtThreadNode for a nested node, or the opening card for
// the root) — both anchor at left-7/left-8, the same x as this block's elbows (ml-4 + left-3).
// `railHovered` is the hover state of the rail these cells belong to (the parent node's avatar-down
// line): when true, this level's elbows light. Each child node owns its own rail state, so grandchild
// rails stay dim — the highlight never cascades down the tree.
export function ThreadChildrenBlock(props: {
    nodes: ThreadNode[];
    ctx: ThreadContext;
    railHovered?: boolean;
    // publicIds already on the path from the root down to (and including) the parent of `nodes`.
    // A node whose publicId is already here would reintroduce an ancestor → an infinite render
    // recursion; it is filtered out below. `.size` doubles as the current nesting depth.
    ancestorIds: ReadonlySet<string>;
}) {
    const { ctx, railHovered = false, ancestorIds } = props;
    // Read before the early returns below — hooks may not sit behind a conditional.
    const isMobile = useIsMobile();
    // Stop nesting past the hard depth ceiling (see MAX_RENDER_DEPTH) and drop any child that loops
    // back onto an ancestor. Both guards only ever fire on malformed/cyclic data — a valid acyclic
    // thread within the fetched depth is never touched, so rendered output is unchanged.
    if (ancestorIds.size >= MAX_RENDER_DEPTH) {
        return null;
    }
    const nodes = props.nodes.filter((node) => !(node.publicId && ancestorIds.has(node.publicId)));
    if (nodes.length === 0) {
        return null;
    }

    // `ancestorIds` holds the path down to (and including) these nodes' parent, so its size IS the
    // number of indent levels above this block — the running total, published for descendants.
    const indentPx = ancestorIds.size * (isMobile ? LEVEL_INDENT_PX.mobile : LEVEL_INDENT_PX.desktop);
    const rightInsetPx = ancestorIds.size * LEVEL_RIGHT_INSET_PX;

    return (
        // NO `w-full` here, deliberately. `width: 100%` resolves against the parent's content box and
        // THEN takes `ml-4 sm:ml-5`, so the box overflowed its parent's right edge by the full margin —
        // 20px per level on desktop, compounding with depth — and `max-w-full` cannot clamp it because
        // that is also 100%. A block div already fills its container, so the class was redundant except
        // in combination with the margin, which is exactly where it did harm. Removing it is what makes
        // the right edge land on the card instead of past it (design §11.8).
        <div
            className="ml-4 sm:ml-5 relative min-w-0 max-w-full"
            style={{
                [THREAD_INDENT_CSS_VAR]: `${indentPx}px`,
                [THREAD_RIGHT_INSET_CSS_VAR]: `${rightInsetPx}px`,
            } as React.CSSProperties}
        >
            {nodes.map((node, index) => {
                const isLast = index === nodes.length - 1;
                // DISCUSSION-4, operator decision 2026-08-20 (wave-6 DECISIONS.md §1 A13): the last ~20px
                // of the reply's empty share. The `pb-2` below MUST move with the connector's
                // `bottom-[-0.5rem]` a few lines further down — the rail is drawn to reach into this
                // padding, so changing one alone leaves it either short of the next node or overshooting
                // it. Margin only; no control box is touched, so the 44px coarse-pointer floor holds.
                return (
                    <div key={node.publicId ?? index} className={`relative flex min-w-0 gap-2 ${isLast ? "" : "pb-2"}`}>
                        <div className="relative w-6 shrink-0" aria-hidden="true">
                            <div className={`absolute left-3 top-0 h-5 w-px ${threadConnectorLineClass(railHovered)}`} />
                            <div
                                className={`absolute left-3 top-2 sm:top-3 h-6 w-6 sm:w-7 rounded-bl-xl border-b border-l ${threadConnectorElbowClass(railHovered)}`}
                                data-playwright-id="thought-connector-elbow"
                                data-thought-public-id={node.publicId ?? ""}
                            />
                            {!isLast && (
                                <div className={`absolute left-3 top-2 bottom-[-0.5rem] w-px ${threadConnectorLineClass(railHovered)}`} />
                            )}
                        </div>
                        <div className="min-w-0 flex-1 pt-1 mr-2">
                            <ThoughtThreadNode node={node} ctx={ctx} ancestorIds={ancestorIds} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ThoughtThreadNode(props: { node: ThreadNode; ctx: ThreadContext; ancestorIds: ReadonlySet<string> }) {
    const { node, ctx, ancestorIds } = props;
    const {
        common,
        childrenByParent,
        collapsedIds,
        onToggleCollapsed,
        onReplyPublished,
        activeReplyPublicId,
        onReplyToggled,
    } = ctx;

    const isMobile = useIsMobile();
    // Hover state for THIS node's rail (its avatar-down line + the elbows of its direct replies). Set
    // by the collapse-rail button, or — when this node is at the visual cap and shows "Continue this
    // thread" instead of an inline subtree — by hovering that link's connector. Children own their own
    // rail state, so the highlight never cascades into descendant rails.
    const [railHovered, setRailHovered] = useState(false);
    // Mobile reply opens the editor as a bottom-sheet dialog; keep its controller so re-tapping
    // reuses the same mounted dialog instead of stacking, and so we can clean it up on unmount.
    const replyDialogRef = useRef<DialogController | null>(null);

    const publicId = node.publicId ?? "";
    // Derived, not local state: the tree owns which composer is open (see ThreadContext).
    const showEditor = Boolean(publicId) && activeReplyPublicId === publicId;
    // This node's publicId added to the ancestor path, for the cycle guard one level down.
    const childAncestorIds: ReadonlySet<string> = publicId ? new Set([...ancestorIds, publicId]) : ancestorIds;
    const loadedChildren = (publicId && childrenByParent.get(publicId)) || [];
    const hasLoadedChildren = loadedChildren.length > 0;
    const replyCount = node.replyCount ?? 0;
    const descendantCount = node.descendantCount ?? replyCount;
    // Children exist below the fetched depth (the visual cap) → offer the focused view instead.
    const hasMoreBeyondFetch = !hasLoadedChildren && replyCount > 0 && Boolean(publicId);
    const collapsed = Boolean(publicId) && collapsedIds.has(publicId);

    const handleReplyPublished = useCallback(() => {
        // Close this node's composer, then refresh. Toggling by id is safe even though the subtree
        // reload replaces every node: the owner clears the id, so nothing reopens on the new tree.
        if (publicId && activeReplyPublicId === publicId) {
            onReplyToggled(publicId);
        }
        onReplyPublished();
    }, [publicId, activeReplyPublicId, onReplyToggled, onReplyPublished]);

    // Clean up any open reply dialog when this node unmounts (e.g. tree refresh / navigation).
    useEffect(() => {
        return () => {
            try {
                replyDialogRef.current?.cleanup();
            } catch {
                // ignore teardown errors
            }
            replyDialogRef.current = null;
        };
    }, []);

    const replyHeaderId = "discussion-reply-editor-replying-to";

    const openMobileReplyDialog = useCallback(() => {
        // Reuse the existing (kept-mounted) dialog if it's still mounted; otherwise open a fresh one.
        if (replyDialogRef.current) {
            replyDialogRef.current.open();
            return;
        }
        replyDialogRef.current = showThoughtTextEditorDialog({
            localizer: common.localizer,
            ariaLabelledBy: replyHeaderId,
            isUserLoggedIn: MetaHandler.isUserLoggedIn(),
            loggedInUsername: common.loggedInUsername,
            discussionPublicId: common.discussionPublicId,
            parentArgumentPublicId: publicId,
            agreementAgreementTitle: common.agreementAgreementTitle,
            disagreementAgreementTitle: common.disagreementAgreementTitle,
            replyingToUsername: node.username,
            playwrightIdPrefix: "discussion-reply-editor",
            onThoughtPublished: () => onReplyPublished(),
        });
    }, [common, publicId, node.username, onReplyPublished]);

    const handleDiscussClicked = useCallback(() => {
        if (isMobile) {
            openMobileReplyDialog();
            return;
        }
        if (publicId) {
            onReplyToggled(publicId);
        }
    }, [isMobile, openMobileReplyDialog, publicId, onReplyToggled]);

    // FI-584: "Quote" on a selection inside this reply opens THIS reply's composer with the text quoted.
    // Opens, never toggles: a composer already showing stays open and takes the quote.
    const inlineEditorRef = useRef<ThoughtTextEditorHandle | null>(null);
    const openReplyWithQuote = useCallback((selectionText: string) => {
        if (!publicId || !selectionText) {
            return;
        }
        if (isMobile) {
            openMobileReplyDialog();
            (replyDialogRef.current as (DialogController & { addQuote?: (text?: string) => void }) | null)
                ?.addQuote?.(selectionText);
            return;
        }
        if (activeReplyPublicId !== publicId) {
            onReplyToggled(publicId);
        }
        applyQuoteWhenReady(() => inlineEditorRef.current, selectionText);
    }, [activeReplyPublicId, isMobile, onReplyToggled, openMobileReplyDialog, publicId]);

    // Collapsed: a single header-only row standing in for this reply + its subtree. It renders in the
    // same parent cell as a full row, and the collapsed header reuses the same avatar anchor, so the
    // parent's elbow lines up identically whether this node is open or closed.
    if (collapsed && hasLoadedChildren) {
        return (
            <ThoughtDisplayerInDiscussion
                argument={node as ThoughtInDiscussionDTO}
                conversationId={null}
                discussionSlug={common.discussionSlug}
                discussionTitle={common.discussionTitle}
                isAuthorTheLoggedInUser={node.username === common.loggedInUsername}
                localizer={common.localizer}
                isSensitive={common.isSensitive}
                suppressSpoilerGuard={common.suppressSpoilerGuards}
                collapsed={true}
                collapsedReplyCount={descendantCount}
                onToggleCollapsed={() => onToggleCollapsed(publicId, false)}
            />
        );
    }

    // `?? undefined` keeps the existing "render it non-navigating" behaviour for a missing id, but the
    // decision now comes from the guarded builder instead of a truthiness check that `"null"` passes.
    const continueThreadHref = thoughtPermalinkHref(common.discussionSlug, publicId) ?? undefined;

    // The avatar-down line is drawn whenever something hangs below this node — either its loaded
    // children OR a "Continue this thread" affordance at the visual cap. (Previously gated on
    // hasLoadedChildren only, so the last node before "Continue this thread" had no line tying its
    // avatar down to that elbow.) The collapse rail stays gated on hasLoadedChildren, since only a
    // node with an inline subtree can be collapsed.
    const hasThreadBelow = hasLoadedChildren || hasMoreBeyondFetch;

    return (
        <div className="relative min-w-0" data-playwright-id="thought-thread-node" data-thought-public-id={publicId}>
            {hasLoadedChildren ? (
                /* Clickable collapse rail spanning this node's WHOLE subtree. It sits on the outer node
                    div (not the content wrapper), so bottom-0 reaches the bottom of ThreadChildrenBlock
                    and the entire vertical thread line — including the connector segments running down
                    beside the replies (same x as the rail) — collapses this node, Reddit-style. The
                    visible line geometry is untouched; only the invisible hit area grows. */
                <button
                    type="button"
                    aria-label={common.localizer("replies.hide", replyCount)}
                    onClick={() => onToggleCollapsed(publicId, true)}
                    onMouseEnter={() => setRailHovered(true)}
                    onMouseLeave={() => setRailHovered(false)}
                    className="group/rail absolute left-3 sm:left-4 top-14 bottom-0 z-10 w-6 cursor-pointer touch-manipulation rounded-full bg-transparent [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    data-playwright-id="thought-collapse-rail"
                    data-thought-public-id={publicId}
                >
                    {/* Visible collapse handle pinned on the connector line (left-2 + size-4
                        centers it at the line's 16px-from-rail offset in both breakpoints). */}
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute left-2 top-0 flex size-4 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-colors group-hover/rail:border-foreground/70 group-hover/rail:text-foreground"
                    >
                        <Minus className="size-3" />
                    </span>
                </button>
            ) : null}
            <div className="relative w-full min-w-0 max-w-full">
                {hasThreadBelow ? (
                    <div
                        className={`pointer-events-none absolute left-7 bottom-0 top-7 w-px ${threadConnectorLineClass(railHovered)} sm:left-8 sm:top-8`}
                        aria-hidden="true"
                        data-playwright-id="thought-connector-spine"
                        data-thought-public-id={publicId}
                    />
                ) : null}

                <ThoughtDisplayerInDiscussion
                    argument={node as ThoughtInDiscussionDTO}
                    conversationId={null}
                    discussionSlug={common.discussionSlug}
                    discussionTitle={common.discussionTitle}
                    isAuthorTheLoggedInUser={node.username === common.loggedInUsername}
                    localizer={common.localizer}
                    isSensitive={common.isSensitive}
                    suppressSpoilerGuard={common.suppressSpoilerGuards}
                    onDiscussClicked={handleDiscussClicked}
                    onQuoteSelection={openReplyWithQuote}
                    contentWrapperClassName="ml-10 pr-3 sm:ml-12 sm:pr-4"
                    discussionAuthorUsername={common.discussionAuthorUsername}
                />
            </div>

            {showEditor && !isMobile ? (
                /* The composer is a LEAF — nothing hangs off it and no connector points at it — so it
                   does not need the thread indent it inherits, and at depth that indent leaves it
                   narrower than the message it is answering (measured on prod thread nvrbLkh7BrZa:
                   depth 7 ⇒ ~412px of an ~870px card). Cancel the accumulated indent and use the full
                   card width, WITHOUT touching the rail geometry — the elbow, spine and collapse rail
                   are unchanged, which `discussion-thread-connector-alignment.spec.mjs` pins.
                   The node's own avatar gutter (`ml-10 sm:ml-12`) goes too: at full width the composer
                   aligns to the card, not to the reply's body column. */
                <div
                    className="mt-1"
                    style={{
                        marginLeft: `calc(var(${THREAD_INDENT_CSS_VAR}, 0px) * -1)`,
                        // Both sides, or neither. Cancelling only the left indent pins the composer's
                        // left edge to the card and leaves its right edge 8px per level short of it —
                        // which is what the width assertion caught on its first devo run (§11.8).
                        marginRight: `calc(var(${THREAD_RIGHT_INSET_CSS_VAR}, 0px) * -1)`,
                    }}
                    data-playwright-id="thought-reply-composer-dedented"
                    data-thought-public-id={publicId}
                >
                    <ThoughtTextEditor
                        ref={inlineEditorRef}
                        localizer={common.localizer}
                        agreementAgreementTitle={common.agreementAgreementTitle}
                        disagreementAgreementTitle={common.disagreementAgreementTitle}
                        isUserLoggedIn={MetaHandler.isUserLoggedIn()}
                        loggedInUsername={common.loggedInUsername}
                        discussionPublicId={common.discussionPublicId}
                        parentArgumentPublicId={publicId}
                        autoFocus={true}
                        onThoughtPublished={handleReplyPublished}
                        playwrightIdPrefix="discussion-reply-editor"
                        hideAvatarOnMobile={true}
                    />
                </div>
            ) : null}

            {hasLoadedChildren ? (
                <ThreadChildrenBlock nodes={loadedChildren} ctx={ctx} railHovered={railHovered} ancestorIds={childAncestorIds} />
            ) : null}

            {hasMoreBeyondFetch && continueThreadHref ? (
                // Same construct as ThreadChildrenBlock's wrapper, so the same `w-full` removal — see
                // the comment there. This one holds no composer, but it overflowed identically.
                <div className="ml-4 sm:ml-5 relative min-w-0 max-w-full">
                    <div
                        className="relative flex min-w-0 gap-2"
                        onMouseEnter={() => setRailHovered(true)}
                        onMouseLeave={() => setRailHovered(false)}
                    >
                        <div className="relative w-6 shrink-0" aria-hidden="true">
                            <div className={`absolute left-3 top-0 h-5 w-px ${threadConnectorLineClass(railHovered)}`} />
                            <div className={`absolute left-3 top-2 sm:top-3 h-6 w-6 sm:w-7 rounded-bl-xl border-b border-l ${threadConnectorElbowClass(railHovered)}`} />
                        </div>
                        {/* Vertically centred on the avatar anchor (size-10 sm:size-12 at pt-2, elbow
                            bar ≈ 32/36px) so the elbow meets the link the same way it meets a real
                            reply's author row. */}
                        <div className="min-w-0 flex-1 pb-2 pt-[18px] sm:pt-[22px]">
                            <a
                                href={continueThreadHref}
                                // DISCUSSION-66 — the PRIMARY of the two stacked continuation links, and
                                // the one a fingertip has to hit. It was a bare inline anchor: no
                                // padding and no floor, so its hit box was its ~20 CSS px line box while
                                // every button beside it in the same list inherits 44 px. The floor is
                                // applied on a coarse pointer only, so mouse density is unchanged, and
                                // it is `min-h-11` rather than `touch-hit-44` because an overlay
                                // pseudo-element would collide with the muted sibling ~30 CSS px below
                                // it (the overlap constraint in 03-tap-targets.css:70). Growing the
                                // LAYOUT box rather than an overlay is also what stops the pair sitting
                                // inside each other's reach.
                                className="inline-flex items-center gap-1 text-sm font-medium text-primary no-underline hover:underline touch-manipulation [@media(pointer:coarse)]:min-h-11"
                                data-playwright-id="thought-continue-thread-link"
                                data-thought-public-id={publicId}
                            >
                                {/*
                                  * Says HOW MANY (2026-09-01). `replyCount` is on the wire in the same
                                  * payload that produced this node, is already parsed at the top of this
                                  * component, and already GATES this very affordance -- `hasMoreBeyondFetch`
                                  * is `replyCount > 0`. It was fetched, parsed, used for a boolean and then
                                  * discarded behind the words "Continue this thread", which tell the reader
                                  * nothing about whether one reply or forty are behind the link.
                                  *
                                  * `replies.show` is reused rather than parameterising `thread.continue`:
                                  * it already carries an ICU plural in all 25 catalogs and is already
                                  * invoked with a count by the collapsed header in
                                  * ThoughtDisplayerInDiscussion, so this needs no new copy and no
                                  * translation pass.
                                  */}
                                {common.localizer("replies.show", replyCount)}
                                <ArrowRight className="size-4" aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
